<template>
  <!-- UserBrowser with adapter architecture -->
  <div class="user-browser-wrapper">
    <!-- Main content -->
    <div class="user-browser" :style="cssVars">
      <!-- Loading skeleton rows during initial load -->
      <template v-if="!lifecycle.isInitialized">
        <skeleton-item-row
          v-for="i in 7"
          :key="`skeleton-${i}`"
          icon-size="32px"
          :show-secondary="true"
        />
      </template>

      <!-- User List -->
      <template v-else>
        <item-row
          v-for="user in displayItems"
          :key="user.id"
          :item="user"
          :config="getRowConfig(user)"
          :entity-type="'user'"
          :selected="isSelected(user)"
          :focused="isFocused(user)"
          :disabled="isDisabled(user)"
          :archived="false"
          :show-checkbox="mode === 'multi' && showCheckboxes"
          @item-selected="onItemSelected"
          @item-toggled="onItemToggled"
        />

        <!-- No Results -->
        <EmptySelectionState
          v-if="!displayItems.length"
          :message="emptyMessage"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, nextTick, type PropType } from 'vue';
import { Colors } from '@/api';
import {
  type OrganizationMembership,
  OrganizationMembershipStatus,
} from '@/models/user.models';
import EmptySelectionState from '../../shared/EmptySelectionState.vue';
import ItemRow from '../../shared/ItemRow.vue';
import type { ItemRowConfig } from '../../shared/ItemRow.vue';
import SkeletonItemRow from '../../shared/SkeletonItemRow.vue';

// Import adapter architecture components
import { UserBrowserAdapter } from '../../adapters/UserBrowserAdapter';
import { useBrowserAdapter } from '../../composables/useBrowserAdapter';
import { useSelectionSync } from '../../composables/useSelectionSync';

/**
 * Props
 *
 * Follows StandardBrowserProps pattern - uses generic prop names
 * NOTE: "No item" option is handled by the Selector, NOT the Browser
 */
const props = defineProps({
  // Core props
  mode: {
    type: String as PropType<'single' | 'multi'>,
    default: 'single',
  },
  selectedItem: {
    type: Object as PropType<OrganizationMembership | null>,
    default: null,
  },
  selectedItems: {
    type: Array as PropType<OrganizationMembership[]>,
    default: () => [],
  },
  selectedIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },

  // Filter props
  teamIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  includeArchived: {
    type: Boolean,
    default: false,
  },

  // Display props
  primaryColor: {
    type: String as PropType<Colors>,
    default: Colors.BLUE,
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
});

/**
 * Emits - Standard browser events (generic names)
 */
const emit = defineEmits<{
  (e: 'item-selected', user: OrganizationMembership | null): void;
  (e: 'item-toggled', user: OrganizationMembership, selected: boolean): void;
  (e: 'selection-changed', users: OrganizationMembership[]): void;
}>();

/**
 * Configuration
 */
const config = computed(() => ({
  display: {
    primaryColor: props.primaryColor,
    emptyMessage: 'No users found',
  },
  filters: {
    includeArchived: props.includeArchived,
    teamIds: props.teamIds,
  },
  selection: {
    mode: props.mode,
  },
  behavior: {
    showCheckboxes: props.mode === 'multi',
  },
}));

/**
 * Create browser adapter
 */
const adapter = new UserBrowserAdapter(
  {
    getId: (member) => member.userId,
    getDisplayName: (member) =>
      member.user?.name || member.user?.email || 'Unknown',
    getSecondaryText: (member) => member.user?.email || '',
    isArchived: (member) =>
      member.status === OrganizationMembershipStatus.INACTIVE,
  },
  {
    mode: props.mode,
    includeArchived: props.includeArchived,
    teamIds: props.teamIds,
  }
);

// Auto-initialize for standalone usage, but expose initialize for parent control
const browserAdapter = useBrowserAdapter(adapter, true);

// Extract reactive state properly
const state = {
  filteredItems: browserAdapter.filteredItems,
  selectedItems: browserAdapter.selectedItems,
  selectedIds: browserAdapter.selectedIds, // Reactive Set<string> for UI reactivity
  focusedItem: browserAdapter.focusedItem,
  searchQuery: browserAdapter.searchQuery,
  hasEmptySelection: computed(
    () => browserAdapter.selectedIds.value.size === 0
  ),
};

const lifecycle = {
  isInitialized: browserAdapter.isInitialized,
  isLoading: browserAdapter.isLoading,
};

const methods = {
  selectItem: browserAdapter.selectItem,
  toggleItem: browserAdapter.toggleItem,
  setSelectedIds: browserAdapter.setSelectedIds,
  refresh: browserAdapter.refresh,
  search: browserAdapter.search,
  clearSelection: browserAdapter.clearSelection,
};

/**
 * Display items
 */
const displayItems = computed(
  () => state.filteredItems.value as OrganizationMembership[]
);

const emptyMessage = computed(() => config.value.display.emptyMessage);
const showCheckboxes = computed(() => config.value.behavior.showCheckboxes);

/**
 * CSS variables for theming
 */
const cssVars = computed(() => {
  const colorName = config.value.display.primaryColor || Colors.BLUE;
  return {
    '--primary-color': `rgb(var(--tw-color-${colorName}))`,
    '--primary-color-rgb': `var(--tw-color-${colorName})`,
  };
});

/**
 * Get row configuration for ItemRow
 */
function getRowConfig(user: OrganizationMembership): ItemRowConfig {
  const userInfo = user.user;
  const isArchived = user.status === OrganizationMembershipStatus.INACTIVE;

  return {
    icon: {
      type: 'avatar',
      user: user, // Pass the full OrganizationMembership object, not just user.user
      size: '32px',
      hideInactiveWarning: isArchived, // Hide the warning icon for archived users since we use badges instead
    },
    primary: {
      text: userInfo?.name || userInfo?.email || 'Unknown User',
      className: 'tw-text-sm tw-font-medium',
    },
    secondary: {
      text: userInfo?.email || '',
      className: 'tw-text-xs tw-text-gray-medium',
    },
    badge: isArchived
      ? {
          text: 'Archived',
          color: 'red', // Single color for both text and background (20% opacity)
        }
      : undefined,
  };
}

/**
 * Selection helpers
 */
function isSelected(user: OrganizationMembership): boolean {
  // CRITICAL: Read directly from props for immediate reactivity
  if (props.mode === 'single') {
    return props.selectedItem?.userId === user.userId;
  }
  // Multi mode: check selectedItems first, then fall back to selectedIds
  if (props.selectedItems && props.selectedItems.length > 0) {
    return props.selectedItems.some((u) => u.userId === user.userId);
  }
  return (props.selectedIds || []).includes(user.userId);
}

function isFocused(user: OrganizationMembership): boolean {
  return state.focusedItem.value?.userId === user.userId;
}

function isDisabled(user: OrganizationMembership): boolean {
  return (
    user.status === OrganizationMembershipStatus.INACTIVE &&
    !config.value.filters.includeArchived
  );
}

/**
 * Event handlers
 */
function onItemSelected(user: OrganizationMembership): void {
  if (isDisabled(user)) return;

  if (config.value.selection.mode === 'single') {
    methods.selectItem(user);
    emit('item-selected', user);
  } else {
    onItemToggled(user);
  }
}

function onItemToggled(user: OrganizationMembership, selected?: boolean): void {
  if (isDisabled(user)) return;

  // Check current state before any action
  const isCurrentlySelected = adapter.isItemSelected(user);

  // Determine the final state we want to achieve
  const targetState = selected !== undefined ? selected : !isCurrentlySelected;

  // Only toggle if we need to change state
  if (targetState !== isCurrentlySelected) {
    methods.toggleItem(user);
  }

  // Always emit the target state immediately so parent can update its UI
  emit('item-toggled', user, targetState);

  const selectedUsers = state.selectedItems.value as OrganizationMembership[];
  emit('selection-changed', selectedUsers);
}

/**
 * Watch for external selection changes
 */
watch(
  () => props.selectedItem,
  (user) => {
    if (user && config.value.selection.mode === 'single') {
      // Select by userId directly - don't require user to be in displayItems
      // The adapter will handle the selection state correctly
      adapter.selectItem(user);
    } else if (!user && config.value.selection.mode === 'single') {
      // Clear selection if user is null
      adapter.clearSelection();
    }
  },
  { immediate: true }
);

// Use unified selection sync composable (Pattern B: initialize-once)
// Match PriorityBrowser pattern - selection state is read from props, not adapter
// Adapter state is only used for navigation/focus, so one-time init is sufficient
useSelectionSync({
  adapter,
  mode: config.value.selection.mode,
  syncStrategy: 'initialize-once',
  selectedItem: props.selectedItem,
  selectedItems: props.selectedItems,
});

/**
 * Watch for filter changes
 */
watch(
  () => config.value.filters,
  async (filters) => {
    if (adapter) {
      await adapter.setIncludeArchived(filters.includeArchived || false);
      if ('setTeamFilter' in adapter) {
        await adapter.setTeamFilter(filters.teamIds || []);
      }
    }
  },
  { deep: true }
);

/**
 * Public API
 */
defineExpose({
  // Standard browser API
  initialize: browserAdapter.initialize,
  refresh: methods.refresh,
  search: methods.search,
  setSearchQuery: methods.search, // Standard interface requirement
  clearSelection: methods.clearSelection,
  getSelectedUsers: () => state.selectedItems.value as OrganizationMembership[],
  getSearchResultsCount: () => state.filteredItems.value.length,
  scrollToFirstSelected: async () => {
    await nextTick();
    const selected = document.querySelector(
      '.user-browser [data-navigable].item-selector__content-item--selected'
    ) as HTMLElement;
    selected?.scrollIntoView({ block: 'nearest' });
    return !!selected;
  },
  getFocusedUser: () => state.focusedItem.value,

  // Focus management (standard interface)
  handleFocusFirst: () => {
    const firstItem = document.querySelector('[data-navigable="true"]');
    (firstItem as HTMLElement)?.focus();
  },
  handleFocusLast: () => {
    const items = document.querySelectorAll('[data-navigable="true"]');
    const lastItem = items[items.length - 1];
    (lastItem as HTMLElement)?.focus();
  },
  handleFocusLastFromFooter: () => {
    const items = document.querySelectorAll('[data-navigable="true"]');
    const lastItem = items[items.length - 1];
    (lastItem as HTMLElement)?.focus();
  },

  // Legacy aliases for backward compatibility
  focusFirst: () => {
    const firstItem = document.querySelector('[data-navigable="true"]');
    (firstItem as HTMLElement)?.focus();
  },
  focusLast: () => {
    const items = document.querySelectorAll('[data-navigable="true"]');
    const lastItem = items[items.length - 1];
    (lastItem as HTMLElement)?.focus();
  },
});
</script>

<style scoped lang="scss">
@import '../../styles/modal-selectors.scss';

// The base styles from browser-base.scss handle full height
// Additional user-specific styles can be added here if needed

.item-selector__content-item {
  @apply tw-p-3;
  min-height: 56px; // Larger height to accommodate user avatars (32px + padding)
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(var(--primary-color-rgb), 0.05);
  }

  &:focus {
    @apply tw-outline-none;
    box-shadow: inset 0 0 0 1px var(--primary-color);
    z-index: 1;
  }

  &--selected {
    background-color: rgba(var(--primary-color-rgb), 0.1);
  }

  &--selected:hover {
    background-color: rgba(var(--primary-color-rgb), 0.15);
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
